import {
  clearQuoteToken,
  getClientFacingMessage,
  MY_ESTIMATE_REQUEST_STATUS_LABELS,
  readQuoteToken,
  useDeclinePublicQuoteMutation,
  useDownloadPublicQuotePdfMutation,
  useGetMyEstimatesQuery,
  useGetPublicQuoteQuery,
  useGrantQuoteAccessMutation,
  useRequestPublicQuoteModificationMutation,
  useRequestPublicQuoteSimilarMutation,
} from '@massilia/queries'
import { Button, downloadBlob, StatusBadge } from '@massilia/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarClock, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { PersonalDataAccessForm } from '@/components/access/personal-data-access-form'
import { AcceptEstimateDialog } from '@/components/estimates/accept-estimate-dialog'
import { DeclineEstimateDialog } from '@/components/estimates/decline-estimate-dialog'
import { EstimateResponseCard } from '@/components/estimates/estimate-response-card'
import { RequestModificationDialog } from '@/components/estimates/request-modification-dialog'
import { similarRequestFor } from '@/components/estimates/similar-request'
import { TariffsLaterNotice } from '@/components/estimates/tariffs-later-notice'
import { useAuthStore } from '@/stores/auth.store'

const searchSchema = z.object({
  /** Jeton du mail. Retiré de l'URL dès qu'il a été échangé. */
  t: z.string().optional(),
})

/**
 * `devis_` et non `devis` : sans le tiret bas, TanStack imbrique cette page sous
 * `/devis`, le formulaire public de demande de devis — qui ne rend aucun
 * `<Outlet/>`. Le lien du mail affichait alors le formulaire de demande à la
 * place de la proposition, sans la moindre erreur.
 */
export const Route = createFileRoute('/_blue_header/devis_/$estimateResponseId')({
  component: PublicQuotePage,
  validateSearch: searchSchema,
})

const ACCESS_ERROR = 'Proposition introuvable ou nom incorrect.'

export function PublicQuotePage() {
  const { estimateResponseId } = Route.useParams()
  const { t } = Route.useSearch()
  const navigate = useNavigate()

  const { isAuthenticated } = useAuthStore()
  const { data: myEstimates } = useGetMyEstimatesQuery(isAuthenticated)

  const [hasAccess, setHasAccess] = useState(() => readQuoteToken(estimateResponseId) !== null)
  const [accessError, setAccessError] = useState<null | string>(null)
  const [dialog, setDialog] = useState<'accept' | 'decline' | 'modification' | null>(null)

  const grantAccess = useGrantQuoteAccessMutation()
  const decline = useDeclinePublicQuoteMutation()
  const requestModification = useRequestPublicQuoteModificationMutation()
  const requestSimilar = useRequestPublicQuoteSimilarMutation()
  const downloadPdf = useDownloadPublicQuotePdfMutation()

  const { data, error, isLoading } = useGetPublicQuoteQuery(estimateResponseId, hasAccess)

  /**
   * Le jeton du mail est échangé UNE fois, puis retiré de l'URL : un lien
   * recopié ou partagé après coup ne doit pas rouvrir le devis tout seul.
   *
   * La ref garde la PROMESSE de l'échange, pas seulement le jeton. Deux raisons,
   * et la seconde nous a coûté un lien mort :
   *
   * - rejouer l'échange à chaque rendu consommerait le quota de vérification du
   *   client (5 essais / 15 min) et le lui refuserait ensuite pour de bon ;
   * - les rappels passés à `mutate` sont abandonnés si le composant se démonte
   *   avant la réponse — ce que fait React en développement (StrictMode), et ce
   *   que peut faire n'importe quel re-rendu de route. Le jeton finissait bien
   *   en session, mais `hasAccess` restait faux : le client voyait la
   *   vérification par nom alors que son lien était bon. En rattachant les
   *   suites à la MÊME promesse, le remontage récupère le résultat sans
   *   relancer d'appel.
   */
  const exchange = useRef<null | { promise: Promise<unknown>; token: string }>(null)
  useEffect(() => {
    if (!t) return

    if (exchange.current?.token !== t) {
      exchange.current = {
        promise: grantAccess.mutateAsync({ responseId: estimateResponseId, token: t }),
        token: t,
      }
    }

    let abandoned = false
    void exchange.current.promise
      .then(() => !abandoned && setHasAccess(true))
      .catch(() => !abandoned && setAccessError(ACCESS_ERROR))
      // Le `?t=` ne part qu'une fois l'échange terminé : plus tôt, le re-rendu
      // de route emporterait l'échange avec lui.
      .finally(() => !abandoned && void navigate({ replace: true, search: {}, to: '.' }))

    return () => {
      abandoned = true
    }
  }, [estimateResponseId, grantAccess, navigate, t])

  // Un client connecté à qui appartient la proposition n'a rien à faire ici :
  // son espace lui montre la demande entière, pas cette proposition seule.
  useEffect(() => {
    const isMine = myEstimates?.some((group) =>
      group.responses.some((response) => response.id === estimateResponseId)
    )

    if (!isMine) return

    void navigate({ hash: estimateResponseId, replace: true, to: '/mes-devis' })
  }, [estimateResponseId, myEstimates, navigate])

  // Un jeton périmé n'a pas à laisser la page en chargement : on le purge, et le
  // rendu repasse aussitôt par le mur du nom — `tokenExpired` ci-dessous. Purger
  // sans toucher à l'état : la remise à zéro se déduit de l'erreur, elle n'a pas
  // à être recopiée dans deux `useState` au prix d'un rendu en cascade.
  useEffect(() => {
    if (!error) return

    clearQuoteToken(estimateResponseId)
  }, [error, estimateResponseId])

  const tokenExpired = Boolean(error)

  if (!hasAccess || tokenExpired) {
    return (
      <PersonalDataAccessForm
        description="Pour y accéder, indiquez le nom de famille du client ou d’un voyageur."
        errorMessage={tokenExpired ? ACCESS_ERROR : accessError}
        isSubmitting={grantAccess.isPending}
        onSubmit={(credentials) =>
          grantAccess.mutate(
            { ...credentials, responseId: estimateResponseId },
            {
              // Le message du serveur, quand il en a rédigé un : un calcul raté
              // se dit, il ne se confond pas avec un nom inconnu.
              onError: (failure) => setAccessError(getClientFacingMessage(failure, ACCESS_ERROR)),
              onSuccess: () => {
                setAccessError(null)
                setHasAccess(true)
              },
            }
          )
        }
        submitLabel="Voir ma proposition"
        title="Votre proposition de voyage"
      />
    )
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center py-14">
        <Loader2 className="size-8 animate-spin text-brand-600" />
      </div>
    )
  }

  const { request, response } = data

  return (
    <div className="flex w-full flex-col gap-6">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-gray-light-900">{request.route}</h1>
            <p className="text-sm text-gray-light-600">
              {request.type === 'FLIGHT' ? 'Vol' : 'Séjour'} · {request.passengerCount} voyageur
              {request.passengerCount > 1 ? 's' : ''}
            </p>
          </div>
          {/* Le statut de la DEMANDE n'apparaît ici que lorsqu'il change la
              lecture de la proposition — « Tarifs indisponibles » signifie que
              l'agence attend la publication des tarifs pour la suite. */}
          {request.status === 'TARIFFS_LATER' && (
            <StatusBadge appearance="solid" icon=<CalendarClock aria-hidden /> tone="warning">
              {MY_ESTIMATE_REQUEST_STATUS_LABELS.TARIFFS_LATER}
            </StatusBadge>
          )}
        </div>
        <TariffsLaterNotice group={request} />
      </header>

      <div className="overflow-hidden rounded-2xl border border-gray-light-200">
        <EstimateResponseCard
          actions={{
            onAccept: () => setDialog('accept'),
            onDecline: () => setDialog('decline'),
            onDownloadPdf: () =>
              downloadPdf.mutate(estimateResponseId, {
                onError: () => toast.error('Impossible de télécharger le devis'),
                onSuccess: (blob) => downloadBlob(blob, 'devis.pdf'),
              }),
            onRequestModification: () => setDialog('modification'),
          }}
          isDownloadingPdf={downloadPdf.isPending}
          requestSimilar={similarRequestFor(requestSimilar, estimateResponseId)}
          response={response}
        />
      </div>

      {/* La page publique ne montre qu'une proposition : c'est exactement ce que
          le compte apporte en plus, et c'est ce que dit le bandeau. */}
      <aside className="flex flex-col items-start gap-3 rounded-2xl bg-brand-50 p-6 md:flex-row md:items-center md:justify-between">
        <p className="text-sm font-medium text-gray-light-900">
          Vous avez un compte ? Connectez-vous pour retrouver toutes vos demandes et propositions.
        </p>
        <Button asChild className="bg-brand-600 text-white hover:bg-brand-700">
          <Link search={{ redirect: '/mes-devis' }} to="/connexion">
            Se connecter
          </Link>
        </Button>
      </aside>

      <AcceptEstimateDialog
        isOpen={dialog === 'accept'}
        onConfirm={() => {
          void navigate({
            params: { estimateResponseId },
            to: '/devis/$estimateResponseId/reserver',
          })
        }}
        onOpenChange={(open) => !open && setDialog(null)}
        // La page publique ignore les autres propositions de la demande : on ne
        // peut donc pas annoncer un compte, et annoncer « 0 » serait faux.
        otherLiveResponsesCount={0}
        responseName={response.name}
      />

      <DeclineEstimateDialog
        isOpen={dialog === 'decline'}
        isSubmitting={decline.isPending}
        onConfirm={(payload) =>
          decline.mutate(
            { ...payload, responseId: estimateResponseId },
            {
              onError: () => toast.error('Impossible d’enregistrer votre refus'),
              onSuccess: () => {
                toast.success('Votre refus a bien été transmis')
                setDialog(null)
              },
            }
          )
        }
        onOpenChange={(open) => !open && setDialog(null)}
        responseName={response.name}
      />

      <RequestModificationDialog
        isOpen={dialog === 'modification'}
        isSubmitting={requestModification.isPending}
        onConfirm={(payload) =>
          requestModification.mutate(
            { ...payload, responseId: estimateResponseId },
            {
              onError: () => toast.error('Impossible d’envoyer votre demande'),
              onSuccess: () => {
                toast.success('Votre demande a bien été transmise')
                setDialog(null)
              },
            }
          )
        }
        onOpenChange={(open) => !open && setDialog(null)}
        responseName={response.name}
      />
    </div>
  )
}
